<template>
  <div class="import-data-page">
    <v-row class="justify-center">
      <v-col
        cols="12"
        md="10"
        lg="8"
      >
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon
              class="me-2"
              color="primary"
            >
              {{ icons.mdiDatabaseImportOutline }}
            </v-icon>
            Import Facebook Data
          </v-card-title>
          <v-card-subtitle>
            Import your photos, posts, and life events from a Facebook data export
          </v-card-subtitle>

          <v-card-text>
            <!-- ERROR -->
            <v-alert
              v-if="error"
              type="error"
              dismissible
              class="mb-4"
              @input="error = null"
            >
              {{ error }}
            </v-alert>

            <!-- STATE: idle -->
            <div v-if="state === 'idle'">
              <v-alert
                type="info"
                outlined
                class="mb-4"
              >
                <div class="font-weight-bold mb-2">
                  How to download your Facebook data:
                </div>
                <ol class="pl-4">
                  <li>Go to <a href="https://www.facebook.com/dyi/?referrer=yfi_settings" target="_blank" rel="noopener noreferrer"><strong>Facebook - Download Your Information</strong></a></li>
                  <li>Select <strong>JSON</strong> format (not HTML)</li>
                  <li>Select the data you want: <strong>Posts</strong>, <strong>Photos and Videos</strong></li>
                  <li>Click <strong>Create File</strong> and wait for the download to be ready</li>
                  <li>Download the ZIP file and upload it below</li>
                </ol>
              </v-alert>

              <v-file-input
                v-model="file"
                accept=".zip"
                label="Select your Facebook data ZIP file"
                outlined
                prepend-icon=""
                :prepend-inner-icon="icons.mdiFileArchiveOutline"
                show-size
                @change="handleFile"
              ></v-file-input>
            </div>

            <!-- STATE: parsing -->
            <div v-if="state === 'parsing'">
              <div class="text-center py-8">
                <v-icon
                  size="48"
                  color="primary"
                  class="mb-4"
                >
                  {{ icons.mdiDatabaseSearchOutline }}
                </v-icon>
                <h3 class="mb-4">
                  Analyzing your Facebook data...
                </h3>
                <v-progress-linear
                  :value="parseProgress"
                  color="primary"
                  height="8"
                  rounded
                  class="mb-2"
                ></v-progress-linear>
                <p class="text--secondary">
                  {{ parseStatus }}
                </p>
              </div>
            </div>

            <!-- STATE: preview -->
            <div v-if="state === 'preview'">
              <h3 class="mb-4">
                Data found in your export:
              </h3>

              <v-row class="mb-4">
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    :class="{ 'border-primary': selectedPhotosCount > 0 }"
                  >
                    <v-card-text class="text-center">
                      <v-checkbox
                        :input-value="isTypeFullySelected('photo')"
                        :indeterminate="isTypePartiallySelected('photo')"
                        hide-details
                        class="mt-0 pt-0 d-inline-flex"
                        :disabled="photos.length === 0"
                        @change="setTypeSelection('photo', $event)"
                      >
                        <template v-slot:label>
                          <div class="text-center">
                            <v-icon
                              size="36"
                              :color="photos.length ? 'success' : 'grey'"
                              class="d-block mb-1"
                            >
                              {{ icons.mdiImageMultipleOutline }}
                            </v-icon>
                            <div class="text-h6">
                              {{ selectedPhotosCount }} / {{ photos.length }} Photos
                            </div>
                            <div class="text-caption text--secondary">
                              → My Pictures
                            </div>
                          </div>
                        </template>
                      </v-checkbox>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    :class="{ 'border-primary': selectedPostsCount > 0 }"
                  >
                    <v-card-text class="text-center">
                      <v-checkbox
                        :input-value="isTypeFullySelected('post')"
                        :indeterminate="isTypePartiallySelected('post')"
                        hide-details
                        class="mt-0 pt-0 d-inline-flex"
                        :disabled="posts.length === 0"
                        @change="setTypeSelection('post', $event)"
                      >
                        <template v-slot:label>
                          <div class="text-center">
                            <v-icon
                              size="36"
                              :color="posts.length ? 'success' : 'grey'"
                              class="d-block mb-1"
                            >
                              {{ icons.mdiTextBoxOutline }}
                            </v-icon>
                            <div class="text-h6">
                              {{ selectedPostsCount }} / {{ posts.length }} Posts
                            </div>
                            <div class="text-caption text--secondary">
                              → My Diary
                            </div>
                          </div>
                        </template>
                      </v-checkbox>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <v-card
                    outlined
                    :class="{ 'border-primary': selectedLifeEventsCount > 0 }"
                  >
                    <v-card-text class="text-center">
                      <v-checkbox
                        :input-value="isTypeFullySelected('lifeEvent')"
                        :indeterminate="isTypePartiallySelected('lifeEvent')"
                        hide-details
                        class="mt-0 pt-0 d-inline-flex"
                        :disabled="lifeEvents.length === 0"
                        @change="setTypeSelection('lifeEvent', $event)"
                      >
                        <template v-slot:label>
                          <div class="text-center">
                            <v-icon
                              size="36"
                              :color="lifeEvents.length ? 'success' : 'grey'"
                              class="d-block mb-1"
                            >
                              {{ icons.mdiStarOutline }}
                            </v-icon>
                            <div class="text-h6">
                              {{ selectedLifeEventsCount }} / {{ lifeEvents.length }} Life Events
                            </div>
                            <div class="text-caption text--secondary">
                              → My Diary
                            </div>
                          </div>
                        </template>
                      </v-checkbox>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Media preview (filtered, paginated, selectable, hover-to-play video) -->
              <div
                v-if="photos.length"
                class="mb-4"
              >
                <div class="d-flex align-center flex-wrap mb-2">
                  <h4 class="mb-0 me-2">
                    Media Preview
                  </h4>
                  <span class="text-caption text--secondary me-4">
                    {{ filteredPhotos.length }} of {{ photos.length }} match filters · {{ selectedFilteredPhotosCount }} selected · {{ formatBytes(filteredMediaSize) }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn
                    x-small
                    text
                    class="me-1"
                    :disabled="!filteredPhotos.length"
                    @click="setFilteredPhotosSelection(true)"
                  >
                    Select all
                  </v-btn>
                  <v-btn
                    x-small
                    text
                    color="error"
                    :disabled="!filteredPhotos.length"
                    @click="setFilteredPhotosSelection(false)"
                  >
                    Deselect all
                  </v-btn>
                </div>
                <div
                  v-if="!filteredPhotos.length"
                  class="text-caption text--secondary py-4 text-center"
                >
                  No media matches the current filters.
                </div>
                <div
                  v-else
                  class="d-flex flex-wrap media-preview-grid"
                >
                  <div
                    v-for="photo in pagedPhotos"
                    :key="photo.__id"
                    class="ma-1 media-thumb"
                    :class="{ 'media-thumb--selected': isEntrySelected(photo.__id) }"
                    :title="`${photo.title || ''}${photo.__size ? ' (' + formatBytes(photo.__size) + ')' : ''}`"
                    @click="setEntrySelection(photo.__id, !isEntrySelected(photo.__id))"
                    @mouseenter="onThumbMouseEnter(photo)"
                    @mouseleave="onThumbMouseLeave(photo)"
                  >
                    <video
                      v-if="photo.__isVideo && photoBlobUrls[photo.__id]"
                      :ref="`thumb-video-${photo.__id}`"
                      :src="photoBlobUrls[photo.__id]"
                      class="media-thumb-media"
                      muted
                      loop
                      playsinline
                      preload="metadata"
                    ></video>
                    <v-img
                      v-else-if="photoBlobUrls[photo.__id]"
                      :src="photoBlobUrls[photo.__id]"
                      width="96"
                      height="96"
                      class="rounded media-thumb-media"
                      cover
                    ></v-img>
                    <div
                      v-else
                      class="media-thumb-media rounded grey lighten-3 d-flex align-center justify-center"
                    >
                      <v-progress-circular
                        indeterminate
                        size="20"
                        width="2"
                        color="grey"
                      ></v-progress-circular>
                    </div>
                    <v-icon
                      v-if="photo.__isVideo && hoveredMediaId !== photo.__id"
                      class="media-thumb-video-icon"
                      color="white"
                      size="36"
                    >
                      {{ icons.mdiPlayCircle }}
                    </v-icon>
                    <v-icon
                      class="media-thumb-check"
                      :color="isEntrySelected(photo.__id) ? 'primary' : 'grey lighten-1'"
                      size="18"
                    >
                      {{ isEntrySelected(photo.__id) ? icons.mdiCheckboxMarked : icons.mdiCheckboxBlankOutline }}
                    </v-icon>
                    <span
                      v-if="photo.__size"
                      class="media-thumb-size"
                    >{{ formatBytes(photo.__size) }}</span>
                  </div>
                </div>
                <div
                  v-if="mediaPageCount > 1"
                  class="d-flex justify-center mt-2"
                >
                  <v-pagination
                    v-model="mediaPage"
                    :length="mediaPageCount"
                    :total-visible="7"
                  ></v-pagination>
                </div>
              </div>

              <!-- Approval controls -->
              <div
                v-if="allEntries.length"
                class="mb-4"
              >
                <h4 class="mb-2">
                  Approve Entries to Import:
                </h4>

                <v-row class="mb-2">
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="filters.year"
                      :items="yearFilterOptions"
                      label="Year"
                      dense
                      outlined
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="filters.types"
                      :items="typeFilterOptions"
                      label="Data Type"
                      dense
                      outlined
                      multiple
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="filters.query"
                      label="Search title or description"
                      dense
                      outlined
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <div class="d-flex align-center flex-wrap mb-2">
                  <v-checkbox
                    :input-value="areAllFilteredSelected"
                    :indeterminate="areSomeFilteredSelected"
                    hide-details
                    class="mt-0 me-4"
                    label="Select all filtered"
                    @change="setFilteredSelection($event)"
                  ></v-checkbox>
                  <v-btn
                    small
                    text
                    class="me-2"
                    @click="setFilteredSelection(true)"
                  >
                    Select Filtered
                  </v-btn>
                  <v-btn
                    small
                    text
                    color="error"
                    @click="setFilteredSelection(false)"
                  >
                    Deselect Filtered
                  </v-btn>
                </div>

                <v-data-table
                  :headers="approvalHeaders"
                  :items="filteredEntries"
                  :items-per-page="10"
                  dense
                  outlined
                  class="rounded"
                >
                  <template v-slot:item.approved="{ item }">
                    <v-checkbox
                      :input-value="isEntrySelected(item.id)"
                      hide-details
                      class="ma-0 pa-0"
                      @change="setEntrySelection(item.id, $event)"
                    ></v-checkbox>
                  </template>

                  <template v-slot:item.type="{ item }">
                    <v-menu
                      open-on-hover
                      right
                      offset-x
                      :close-delay="100"
                      :open-delay="120"
                      :nudge-right="8"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          x-small
                          outlined
                          class="approval-type-hover"
                          v-bind="attrs"
                          v-on="on"
                          @mouseenter="ensureHoverPreview(item)"
                        >
                          <v-icon
                            v-if="item.isVideo"
                            x-small
                            left
                          >
                            {{ icons.mdiVideoOutline }}
                          </v-icon>
                          {{ item.isVideo ? 'Video' : typeLabel(item.type) }}
                        </v-chip>
                      </template>

                      <v-card class="import-hover-preview-card">
                        <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
                          Entry Preview
                        </v-card-title>
                        <v-card-text class="pt-1">
                          <div class="text-caption mb-1">
                            <strong>Type:</strong> {{ typeLabel(item.type) }}
                          </div>
                          <div class="text-caption mb-1">
                            <strong>Date:</strong> {{ item.date || '-' }}
                          </div>
                          <div class="text-caption mb-3">
                            <strong>Year:</strong> {{ item.year || '-' }}
                          </div>

                          <div
                            v-if="item.type === 'photo'"
                            class="text-caption mb-3"
                          >
                            <strong>Estimated Size:</strong>
                            {{ item.size ? formatBytes(item.size) : '-' }}
                            <span v-if="item.isVideo" class="ms-1">(video)</span>
                          </div>

                          <div
                            v-if="hoverPreviewLoading[item.id]"
                            class="text-caption mb-2"
                          >
                            Loading {{ item.isVideo ? 'video' : 'image' }} preview...
                          </div>

                          <div
                            v-if="item.type === 'photo' && hoverPreviewIsVideo[item.id] && previewImages(item).length"
                            class="mb-3"
                          >
                            <div class="text-caption font-weight-bold mb-1">
                              Video
                            </div>
                            <video
                              :src="previewImages(item)[0]"
                              class="hover-preview-video rounded"
                              autoplay
                              muted
                              loop
                              playsinline
                              controls
                            ></video>
                          </div>

                          <div
                            v-else-if="previewImages(item).length"
                            class="mb-3"
                          >
                            <div class="text-caption font-weight-bold mb-1">
                              Pictures
                            </div>
                            <div class="d-flex flex-wrap">
                              <v-img
                                v-for="(img, imgIndex) in previewImages(item)"
                                :key="`import-hover-img-${item.id}-${imgIndex}`"
                                :src="img"
                                width="140"
                                height="140"
                                class="ma-1 rounded"
                                cover
                              ></v-img>
                            </div>
                          </div>

                          <div class="text-caption font-weight-bold mb-1">
                            Description
                          </div>
                          <div
                            class="import-hover-preview-description"
                            v-html="item.description || '<em>No description</em>'"
                          ></div>

                          <div class="text-caption font-weight-bold mt-3 mb-1">
                            Entry Info
                          </div>
                          <pre class="import-hover-preview-json">{{ buildEntryPreviewInfo(item) }}</pre>
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </template>

                  <template v-slot:item.title="{ item }">
                    <div>
                      <div class="font-weight-medium">
                        {{ item.title || '(No title)' }}
                      </div>
                      <div class="text-caption text--secondary text-truncate approval-description">
                        {{ plainText(item.description) || '-' }}
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.date="{ item }">
                    {{ item.date || '-' }}
                  </template>

                  <template v-slot:item.year="{ item }">
                    {{ item.year }}
                  </template>

                  <template v-slot:item.size="{ item }">
                    {{ item.size ? formatBytes(item.size) : '-' }}
                  </template>
                </v-data-table>
              </div>

              <div class="d-flex align-center justify-end mt-4 flex-wrap">
                <div
                  v-if="selectedMediaSize > 0"
                  class="text-caption text--secondary me-auto"
                >
                  Estimated media size:
                  <strong>{{ formatBytes(selectedMediaSize) }}</strong>
                  <span v-if="selectedVideosCount > 0">
                    ({{ selectedVideosCount }} video{{ selectedVideosCount === 1 ? '' : 's' }})
                  </span>
                </div>
                <v-btn
                  text
                  class="me-2"
                  @click="reset()"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="selectedEntriesCount === 0"
                  @click="startImport()"
                >
                  <v-icon left>
                    {{ icons.mdiDatabaseImportOutline }}
                  </v-icon>
                  Start Import ({{ selectedEntriesCount }})
                </v-btn>
              </div>
            </div>

            <!-- STATE: importing -->
            <div v-if="state === 'importing'">
              <div class="text-center py-8">
                <v-icon
                  size="48"
                  color="primary"
                  class="mb-4 rotating-icon"
                >
                  {{ icons.mdiCloudUploadOutline }}
                </v-icon>
                <h3 class="mb-2">
                  {{ importPhase }}
                </h3>
                <p class="text--secondary mb-4">
                  {{ importDetail }}
                </p>
                <v-progress-linear
                  :value="importPercent"
                  color="primary"
                  height="8"
                  rounded
                  class="mb-2"
                ></v-progress-linear>
                <p class="text-caption text--secondary">
                  {{ importCurrent }} of {{ importTotal }}
                </p>
                <v-btn
                  text
                  color="error"
                  class="mt-4"
                  @click="cancelImport()"
                >
                  Cancel
                </v-btn>
              </div>
            </div>

            <!-- STATE: complete -->
            <div v-if="state === 'complete'">
              <div class="text-center py-8">
                <v-icon
                  size="64"
                  color="success"
                  class="mb-4"
                >
                  {{ icons.mdiCheckCircleOutline }}
                </v-icon>
                <h3 class="mb-4">
                  Import Complete!
                </h3>
                <v-row class="justify-center mb-6">
                  <v-col
                    v-if="results.photos > 0"
                    cols="auto"
                  >
                    <v-chip
                      color="primary"
                      outlined
                      large
                    >
                      <v-icon left>
                        {{ icons.mdiImageMultipleOutline }}
                      </v-icon>
                      {{ results.photos }} photos imported
                    </v-chip>
                  </v-col>
                  <v-col
                    v-if="results.diary > 0"
                    cols="auto"
                  >
                    <v-chip
                      color="primary"
                      outlined
                      large
                    >
                      <v-icon left>
                        {{ icons.mdiTextBoxOutline }}
                      </v-icon>
                      {{ results.diary }} diary entries imported
                    </v-chip>
                  </v-col>
                  <v-col
                    v-if="(results.photosSkipped || 0) + (results.diarySkipped || 0) > 0"
                    cols="auto"
                  >
                    <v-chip
                      color="warning"
                      outlined
                      large
                    >
                      {{ (results.photosSkipped || 0) + (results.diarySkipped || 0) }} skipped (already imported)
                    </v-chip>
                  </v-col>
                </v-row>
                <div>
                  <v-btn
                    v-if="results.photos > 0"
                    color="primary"
                    class="me-2"
                    to="/my-pictures"
                  >
                    Go to My Pictures
                  </v-btn>
                  <v-btn
                    v-if="results.diary > 0"
                    color="primary"
                    class="me-2"
                    to="/my-diary"
                  >
                    Go to My Diary
                  </v-btn>
                  <v-btn
                    text
                    @click="reset()"
                  >
                    Import More
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import JSZip from 'jszip'
import axios from 'axios'
import {
  mdiDatabaseImportOutline,
  mdiFileArchiveOutline,
  mdiDatabaseSearchOutline,
  mdiImageMultipleOutline,
  mdiTextBoxOutline,
  mdiStarOutline,
  mdiCloudUploadOutline,
  mdiCheckCircleOutline,
  mdiPlayCircle,
  mdiVideoOutline,
  mdiCheckboxMarked,
  mdiCheckboxBlankOutline,
} from '@mdi/js'

export default {
  data() {
    return {
      icons: {
        mdiDatabaseImportOutline,
        mdiFileArchiveOutline,
        mdiDatabaseSearchOutline,
        mdiImageMultipleOutline,
        mdiTextBoxOutline,
        mdiStarOutline,
        mdiCloudUploadOutline,
        mdiCheckCircleOutline,
        mdiPlayCircle,
        mdiVideoOutline,
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
      },

      state: 'idle', // idle | parsing | preview | importing | complete
      file: null,
      error: null,
      cancelled: false,

      // Parsed data
      zip: null,
      photos: [],
      posts: [],
      lifeEvents: [],
      photoBlobUrls: {},
      hoveredMediaId: null,
      mediaPage: 1,
      mediaPageSize: 24,

      // Parse progress
      parseProgress: 0,
      parseStatus: '',

      // Import options
      selectedEntries: {},
      filters: {
        year: 'All Years',
        types: ['photo', 'post', 'lifeEvent'],
        query: '',
      },
      approvalHeaders: [
        { text: 'Approve', value: 'approved', sortable: false, width: 90 },
        { text: 'Type', value: 'type', width: 120 },
        { text: 'Title', value: 'title' },
        { text: 'Date', value: 'date', width: 140 },
        { text: 'Year', value: 'year', width: 90 },
        { text: 'Size', value: 'size', width: 100 },
      ],
      hoverPreviewImages: {},
      hoverPreviewLoading: {},
      hoverPreviewIsVideo: {},
      totalMediaSize: 0,
      maxThumbs: 48,
      // Import progress
      importPhase: '',
      importDetail: '',
      importCurrent: 0,
      importTotal: 0,

      // Results
      results: { photos: 0, diary: 0, photosSkipped: 0, diarySkipped: 0 },

      endpoint: process.env.VUE_APP_ROOT_API,
    }
  },
  computed: {
    importPercent() {
      return this.importTotal > 0 ? Math.round((this.importCurrent / this.importTotal) * 100) : 0
    },

    allEntries() {
      const photoEntries = this.photos.map(photo => ({
        id: photo.__id,
        type: 'photo',
        title: photo.title,
        description: photo.description,
        date: photo.date,
        year: photo.__year,
        size: photo.__size || 0,
        isVideo: !!photo.__isVideo,
        sourceUrl: photo.__sourceUrl || null,
      }))

      const postEntries = this.posts.map(post => ({
        id: post.__id,
        type: 'post',
        title: post.title,
        description: post.description,
        date: post.date,
        year: post.__year,
        size: 0,
        isVideo: false,
        sourceUrl: post.__sourceUrl || null,
      }))

      const lifeEventEntries = this.lifeEvents.map(event => ({
        id: event.__id,
        type: 'lifeEvent',
        title: event.title,
        description: event.description,
        date: event.date,
        year: event.__year,
        size: 0,
        isVideo: false,
        sourceUrl: event.__sourceUrl || null,
      }))

      return [...photoEntries, ...postEntries, ...lifeEventEntries]
    },

    filteredEntries() {
      const query = (this.filters.query || '').trim().toLowerCase()

      return this.allEntries.filter(entry => {
        const yearMatch = this.filters.year === 'All Years' || entry.year === this.filters.year
        const typeMatch = this.filters.types.includes(entry.type)

        if (!query) return yearMatch && typeMatch

        const haystack = `${entry.title || ''} ${this.plainText(entry.description || '')}`.toLowerCase()
        return yearMatch && typeMatch && haystack.includes(query)
      })
    },

    yearFilterOptions() {
      const years = [...new Set(this.allEntries.map(entry => entry.year))]
      const sortedYears = years
        .filter(year => year !== 'Unknown')
        .sort((a, b) => Number(b) - Number(a))
      if (years.includes('Unknown')) sortedYears.push('Unknown')
      return ['All Years', ...sortedYears]
    },

    typeFilterOptions() {
      return [
        { text: 'Photos', value: 'photo' },
        { text: 'Posts', value: 'post' },
        { text: 'Life Events', value: 'lifeEvent' },
      ]
    },

    selectedEntriesCount() {
      return this.allEntries.reduce((count, entry) => count + (this.isEntrySelected(entry.id) ? 1 : 0), 0)
    },

    selectedPhotosCount() {
      return this.photos.reduce((count, photo) => count + (this.isEntrySelected(photo.__id) ? 1 : 0), 0)
    },

    selectedPostsCount() {
      return this.posts.reduce((count, post) => count + (this.isEntrySelected(post.__id) ? 1 : 0), 0)
    },

    selectedLifeEventsCount() {
      return this.lifeEvents.reduce((count, event) => count + (this.isEntrySelected(event.__id) ? 1 : 0), 0)
    },

    areAllFilteredSelected() {
      if (!this.filteredEntries.length) return false
      return this.filteredEntries.every(entry => this.isEntrySelected(entry.id))
    },

    areSomeFilteredSelected() {
      if (!this.filteredEntries.length) return false
      const selected = this.filteredEntries.filter(entry => this.isEntrySelected(entry.id)).length
      return selected > 0 && selected < this.filteredEntries.length
    },

    selectedMediaSize() {
      return this.photos.reduce((total, photo) => (
        this.isEntrySelected(photo.__id) ? total + (photo.__size || 0) : total
      ), 0)
    },

    selectedVideosCount() {
      return this.photos.reduce((count, photo) => (
        this.isEntrySelected(photo.__id) && photo.__isVideo ? count + 1 : count
      ), 0)
    },

    filteredPhotos() {
      if (!this.filters.types.includes('photo')) return []
      const query = (this.filters.query || '').trim().toLowerCase()
      return this.photos.filter(photo => {
        const yearMatch = this.filters.year === 'All Years' || photo.__year === this.filters.year
        if (!query) return yearMatch
        const haystack = `${photo.title || ''} ${this.plainText(photo.description || '')}`.toLowerCase()
        return yearMatch && haystack.includes(query)
      })
    },

    pagedPhotos() {
      const start = (this.mediaPage - 1) * this.mediaPageSize
      return this.filteredPhotos.slice(start, start + this.mediaPageSize)
    },

    mediaPageCount() {
      return Math.max(1, Math.ceil(this.filteredPhotos.length / this.mediaPageSize))
    },

    filteredMediaSize() {
      return this.filteredPhotos.reduce((total, photo) => total + (photo.__size || 0), 0)
    },

    selectedFilteredPhotosCount() {
      return this.filteredPhotos.reduce((count, photo) => (
        this.isEntrySelected(photo.__id) ? count + 1 : count
      ), 0)
    },
  },
  watch: {
    'filters.year'() { this.mediaPage = 1 },
    'filters.query'() { this.mediaPage = 1 },
    'filters.types'() { this.mediaPage = 1 },
    mediaPageCount(newCount) {
      if (this.mediaPage > newCount) this.mediaPage = newCount
    },
    pagedPhotos: {
      handler(photos) { this.loadThumbsFor(photos) },
      immediate: false,
    },
  },
  beforeDestroy() {
    Object.values(this.hoverPreviewImages).forEach(urls => {
      (urls || []).forEach(url => URL.revokeObjectURL(url))
    })
    Object.values(this.photoBlobUrls).forEach(url => {
      if (url) URL.revokeObjectURL(url)
    })
  },
  methods: {
    reset() {
      this.state = 'idle'
      this.file = null
      this.error = null
      this.cancelled = false
      this.zip = null
      this.photos = []
      this.posts = []
      this.lifeEvents = []
      Object.values(this.photoBlobUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
      this.photoBlobUrls = {}
      this.hoveredMediaId = null
      this.mediaPage = 1
      Object.values(this.hoverPreviewImages).forEach(urls => {
        (urls || []).forEach(url => URL.revokeObjectURL(url))
      })
      this.hoverPreviewImages = {}
      this.hoverPreviewLoading = {}
      this.hoverPreviewIsVideo = {}
      this.totalMediaSize = 0
      this.parseProgress = 0
      this.parseStatus = ''
      this.selectedEntries = {}
      this.filters = {
        year: 'All Years',
        types: ['photo', 'post', 'lifeEvent'],
        query: '',
      }
      this.importPhase = ''
      this.importDetail = ''
      this.importCurrent = 0
      this.importTotal = 0
      this.results = { photos: 0, diary: 0, photosSkipped: 0, diarySkipped: 0 }
    },

    async handleFile(file) {
      if (!file) return
      this.state = 'parsing'
      this.parseProgress = 0
      this.parseStatus = 'Reading ZIP file...'

      try {
        this.zip = await JSZip.loadAsync(file)
        await this.parseZip()
      } catch (err) {
        this.error = 'Failed to parse the ZIP file. Please make sure it is a valid Facebook data export.'
        this.state = 'idle'
      }
    },

    async parseZip() {
      const zip = this.zip
      const jsonFiles = []

      // Collect all JSON files
      zip.forEach((path, entry) => {
        if (!entry.dir && path.toLowerCase().endsWith('.json')) {
          jsonFiles.push({ path, entry })
        }
      })

      if (jsonFiles.length === 0) {
        this.error = 'No data files found in the ZIP. Make sure you selected JSON format when downloading your Facebook data.'
        this.state = 'idle'
        return
      }

      const totalFiles = jsonFiles.length
      let processed = 0
      const photoUris = new Set()

      for (const { path, entry } of jsonFiles) {
        const lowPath = path.toLowerCase()

        try {
          // Posts
          if (lowPath.includes('your_posts') || (lowPath.includes('/posts/') && !lowPath.includes('comments'))) {
            this.parseStatus = `Parsing posts: ${path.split('/').pop()}`
            const content = await entry.async('string')
            const data = JSON.parse(content)
            const items = Array.isArray(data) ? data : (data.status_updates || data.wall_posts || [])

            for (const post of items) {
              // Extract text
              const text = this.getPostText(post)

              // Extract media from attachments
              if (post.attachments) {
                for (const att of post.attachments) {
                  for (const item of (att.data || [])) {
                    if (item.media && item.media.uri && !photoUris.has(item.media.uri)) {
                      photoUris.add(item.media.uri)
                      this.photos.push(this.extractPhotoData(item.media, text))
                    }
                  }
                }
              }

              // Add as diary entry if there's text
              if (text) {
                const timestamp = post.timestamp
                const date = timestamp ? this.formatTimestamp(timestamp) : null
                const decodedText = this.decodeFBText(text)
                const title = decodedText.split('\n')[0].substring(0, 100) || `Facebook Post`
                const externalId = `fb-post-${timestamp || 'notime'}-${this.shortHash(decodedText)}`
                this.posts.push({
                  title,
                  description: decodedText.replace(/\n/g, '<br>'),
                  date,
                  __externalId: externalId,
                  __sourceUrl: this.extractPostUrl(post),
                })
              }
            }
          }

          // Photos
          if (lowPath.includes('your_photos') || lowPath.includes('your_videos')) {
            this.parseStatus = `Parsing photos: ${path.split('/').pop()}`
            const content = await entry.async('string')
            const data = JSON.parse(content)
            const items = Array.isArray(data) ? data : (data.photos || data.videos || [])

            for (const photo of items) {
              if (photo.uri && !photoUris.has(photo.uri)) {
                photoUris.add(photo.uri)
                this.photos.push(this.extractPhotoData(photo))
              }
            }
          }

          // Life events
          if (lowPath.includes('life_events')) {
            this.parseStatus = `Parsing life events: ${path.split('/').pop()}`
            const content = await entry.async('string')
            const data = JSON.parse(content)
            const items = Array.isArray(data) ? data : (data.life_events || [])

            for (const event of items) {
              const timestamp = event.timestamp || event.start_timestamp
              const date = timestamp ? this.formatTimestamp(timestamp) : null
              const title = this.decodeFBText(event.title || '')
              const description = this.decodeFBText(
                (event.data && event.data[0] && event.data[0].life_event && event.data[0].life_event.description) || event.description || '',
              )

              if (title) {
                const externalId = `fb-life-${timestamp || 'notime'}-${this.shortHash(title + '|' + description)}`
                this.lifeEvents.push({
                  title: `Life Event: ${title}`,
                  description: description.replace(/\n/g, '<br>'),
                  date,
                  __externalId: externalId,
                  __sourceUrl: null,
                })
              }
            }
          }
        } catch (parseErr) {
          // Skip files that fail to parse - not all JSON files are data files
          console.warn('Skipping file:', path, parseErr.message)
        }

        processed++
        this.parseProgress = Math.round((processed / totalFiles) * 100)
      }

      // Generate photo thumbnails (first N)
      await this.computeMediaSizes()

      if (this.photos.length === 0 && this.posts.length === 0 && this.lifeEvents.length === 0) {
        this.error = 'No importable data found in the ZIP. Make sure it is a Facebook data export in JSON format.'
        this.state = 'idle'
        return
      }

      this.initializeSelection()
      await this.generateThumbnails()
      this.state = 'preview'
    },

    initializeSelection() {
      const selected = {}
      let idCounter = 1

      const assignMeta = (items, type) => {
        items.forEach(item => {
          const id = `fb-${type}-${idCounter++}`
          this.$set(item, '__id', id)
          this.$set(item, '__year', this.extractYear(item.date))
          selected[id] = true
        })
      }

      assignMeta(this.photos, 'photo')
      assignMeta(this.posts, 'post')
      assignMeta(this.lifeEvents, 'life')
      this.selectedEntries = selected
    },

    extractYear(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return 'Unknown'
      const year = dateStr.split('-')[0]
      return /^\d{4}$/.test(year) ? year : 'Unknown'
    },

    plainText(text) {
      return (text || '').replace(/<br\s*\/?\s*>/gi, ' ')
    },

    isEntrySelected(entryId) {
      return !!this.selectedEntries[entryId]
    },

    setEntrySelection(entryId, selected) {
      this.$set(this.selectedEntries, entryId, !!selected)
    },

    setFilteredSelection(selected) {
      this.filteredEntries.forEach(entry => {
        this.$set(this.selectedEntries, entry.id, !!selected)
      })
    },

    getTypeEntries(type) {
      if (type === 'photo') return this.photos
      if (type === 'post') return this.posts
      if (type === 'lifeEvent') return this.lifeEvents
      return []
    },

    isTypeFullySelected(type) {
      const entries = this.getTypeEntries(type)
      if (!entries.length) return false
      return entries.every(entry => this.isEntrySelected(entry.__id))
    },

    isTypePartiallySelected(type) {
      const entries = this.getTypeEntries(type)
      if (!entries.length) return false
      const selectedCount = entries.filter(entry => this.isEntrySelected(entry.__id)).length
      return selectedCount > 0 && selectedCount < entries.length
    },

    setTypeSelection(type, selected) {
      this.getTypeEntries(type).forEach(entry => {
        this.$set(this.selectedEntries, entry.__id, !!selected)
      })
    },

    typeLabel(type) {
      if (type === 'photo') return 'Photo'
      if (type === 'post') return 'Post'
      if (type === 'lifeEvent') return 'Life Event'
      return type
    },

    findEntrySource(entry) {
      if (!entry || !entry.id) return null

      if (entry.type === 'photo') {
        return this.photos.find(photo => photo.__id === entry.id) || null
      }
      if (entry.type === 'post') {
        return this.posts.find(post => post.__id === entry.id) || null
      }
      if (entry.type === 'lifeEvent') {
        return this.lifeEvents.find(event => event.__id === entry.id) || null
      }

      return null
    },

    extractImageUrlsFromHtml(html) {
      if (!html) return []
      const regex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi
      const urls = []
      let match = regex.exec(html)

      while (match) {
        if (match[1]) urls.push(match[1])
        match = regex.exec(html)
      }

      return urls
    },

    async ensureHoverPreview(entry) {
      if (!entry || !entry.id || entry.type !== 'photo') return
      if (this.hoverPreviewImages[entry.id]) return
      if (this.hoverPreviewLoading[entry.id]) return

      this.$set(this.hoverPreviewLoading, entry.id, true)
      try {
        const source = this.findEntrySource(entry)
        if (!source || !source.uri) {
          this.$set(this.hoverPreviewImages, entry.id, [])
          return
        }

        const zipEntry = this.findFileInZip(source.uri)
        if (!zipEntry) {
          this.$set(this.hoverPreviewImages, entry.id, [])
          return
        }

        const mime = this.getMimeType(source.uri)
        const blob = await zipEntry.async('blob')
        const typedBlob = blob.type ? blob : new Blob([blob], { type: mime })
        const url = URL.createObjectURL(typedBlob)
        this.$set(this.hoverPreviewImages, entry.id, [url])
        this.$set(this.hoverPreviewIsVideo, entry.id, this.isVideoMime(mime))
      } catch (e) {
        this.$set(this.hoverPreviewImages, entry.id, [])
      } finally {
        this.$set(this.hoverPreviewLoading, entry.id, false)
      }
    },

    previewImages(entry) {
      if (!entry) return []
      if (entry.type === 'photo') return this.hoverPreviewImages[entry.id] || []
      return this.extractImageUrlsFromHtml(entry.description)
    },

    buildEntryPreviewInfo(entry) {
      const source = this.findEntrySource(entry) || {}
      const info = {
        id: entry.id,
        type: entry.type,
        title: entry.title,
        date: entry.date,
        year: entry.year,
      }

      if (entry.type === 'photo') {
        info.uri = source.uri || null
        info.location = source.location || null
        info.isVideo = !!source.__isVideo
        info.size = this.formatBytes(source.__size || 0)
      }

      return JSON.stringify(info, null, 2)
    },

    extractPhotoData(media, postText) {
      const timestamp = media.creation_timestamp
      const date = timestamp ? this.formatTimestamp(timestamp) : null
      const title = this.decodeFBText(media.title || '') || `Facebook Photo`
      const description = this.decodeFBText(media.description || postText || '')

      let location = { lat: 0, lng: 0 }
      const mm = media.media_metadata || {}
      const exif = (mm.photo_metadata && mm.photo_metadata.exif_data && mm.photo_metadata.exif_data[0])
        || (mm.video_metadata && mm.video_metadata.exif_data && mm.video_metadata.exif_data[0])
      if (exif && exif.latitude != null && exif.longitude != null) {
        location = { lat: exif.latitude, lng: exif.longitude }
      }

      return {
        uri: media.uri,
        title,
        description: description.replace(/\n/g, '<br>'),
        date,
        location,
        __externalId: `fb-media-${this.shortHash(media.uri)}`,
        __sourceUrl: media.external_url || null,
      }
    },

    extractPostUrl(post) {
      if (!post) return null
      if (post.url) return post.url
      if (post.attachments) {
        for (const att of post.attachments) {
          for (const item of (att.data || [])) {
            if (item.external_context && item.external_context.url) return item.external_context.url
            if (item.place && item.place.url) return item.place.url
          }
        }
      }
      return null
    },

    shortHash(str) {
      const s = String(str || '')
      let h = 0
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h) + s.charCodeAt(i)
        h |= 0
      }
      // Return 8-char unsigned hex
      return (h >>> 0).toString(16).padStart(8, '0')
    },

    getPostText(post) {
      if (post.data) {
        for (const d of post.data) {
          if (d.post) return d.post
          if (d.comment) return d.comment
          if (d.text) return d.text
        }
      }
      if (post.post) return post.post
      if (post.title && !post.attachments) return post.title
      return ''
    },

    async generateThumbnails() {
      // Eagerly load the first page of thumbs so the Media Preview renders immediately
      await this.loadThumbsFor(this.pagedPhotos)
    },

    async loadThumbsFor(photos) {
      for (const photo of photos) {
        if (!photo || !photo.__id) continue
        if (this.photoBlobUrls[photo.__id]) continue
        const entry = this.findFileInZip(photo.uri)
        if (!entry) continue
        try {
          const mime = this.getMimeType(photo.uri)
          const blob = await entry.async('blob')
          const typedBlob = blob.type ? blob : new Blob([blob], { type: mime })
          const url = URL.createObjectURL(typedBlob)
          this.$set(this.photoBlobUrls, photo.__id, url)
        } catch (e) {
          // ignore thumbnail errors
        }
      }
    },

    onThumbMouseEnter(photo) {
      if (!photo || !photo.__isVideo) return
      this.hoveredMediaId = photo.__id
      this.$nextTick(() => {
        const ref = this.$refs[`thumb-video-${photo.__id}`]
        const video = Array.isArray(ref) ? ref[0] : ref
        if (video) {
          try {
            video.currentTime = 0
            const playPromise = video.play()
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {})
          } catch (e) {
            // ignore autoplay errors
          }
        }
      })
    },

    onThumbMouseLeave(photo) {
      if (!photo || !photo.__isVideo) return
      const ref = this.$refs[`thumb-video-${photo.__id}`]
      const video = Array.isArray(ref) ? ref[0] : ref
      if (video) {
        try { video.pause() } catch (e) { /* ignore */ }
      }
      if (this.hoveredMediaId === photo.__id) this.hoveredMediaId = null
    },

    setFilteredPhotosSelection(selected) {
      this.filteredPhotos.forEach(photo => {
        this.$set(this.selectedEntries, photo.__id, !!selected)
      })
    },

    async computeMediaSizes() {
      let total = 0
      for (const photo of this.photos) {
        const entry = this.findFileInZip(photo.uri)
        const size = (entry && entry._data && typeof entry._data.uncompressedSize === 'number')
          ? entry._data.uncompressedSize
          : 0
        this.$set(photo, '__size', size)
        this.$set(photo, '__isVideo', this.isVideoMime(this.getMimeType(photo.uri)))
        total += size
      }
      this.totalMediaSize = total
    },

    isVideoMime(mime) {
      return typeof mime === 'string' && mime.startsWith('video/')
    },

    formatBytes(bytes) {
      if (!bytes || bytes <= 0) return '-'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let value = bytes
      let unitIndex = 0
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
      }
      const decimals = value >= 100 || unitIndex === 0 ? 0 : 1
      return `${value.toFixed(decimals)} ${units[unitIndex]}`
    },

    findFileInZip(uri) {
      if (!uri || !this.zip) return null

      // Try exact path
      let entry = this.zip.file(uri)
      if (entry) return entry

      // Try without the top-level folder prefix
      const parts = uri.split('/')
      if (parts.length > 1) {
        const withoutRoot = parts.slice(1).join('/')
        entry = this.zip.file(withoutRoot)
        if (entry) return entry
      }

      // Try finding by filename match
      const filename = parts[parts.length - 1]
      let found = null
      this.zip.forEach((path, zipEntry) => {
        if (!found && !zipEntry.dir && path.endsWith(filename)) {
          found = zipEntry
        }
      })
      return found
    },

    async startImport() {
      this.state = 'importing'
      this.cancelled = false
      this.results = { photos: 0, diary: 0, photosSkipped: 0, diarySkipped: 0 }

      const photosToImport = this.photos.filter(photo => this.isEntrySelected(photo.__id))
      const diaryEntries = [
        ...this.posts.filter(post => this.isEntrySelected(post.__id)),
        ...this.lifeEvents.filter(event => this.isEntrySelected(event.__id)),
      ]

      this.importTotal = photosToImport.length + (diaryEntries.length > 0 ? 1 : 0)
      this.importCurrent = 0

      try {
        // Phase 1: Upload photos to S3
        if (photosToImport.length > 0) {
          this.importPhase = 'Uploading photos...'
          const uploadedPhotos = await this.uploadPhotos(photosToImport)

          if (this.cancelled) return

          // Phase 2: Save photo metadata in batches
          if (uploadedPhotos.length > 0) {
            this.importPhase = 'Saving photo data...'
            const batchSize = 50
            for (let i = 0; i < uploadedPhotos.length; i += batchSize) {
              if (this.cancelled) return
              const batch = uploadedPhotos.slice(i, i + batchSize)
              this.importDetail = `Batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(uploadedPhotos.length / batchSize)}`

              const response = await axios.post(`${this.endpoint}/importFacebookData`, {
                externalSource: 'facebook',
                photos: batch,
                diary: [],
              }, {
                headers: { Authorization: this.$store.state.jwtToken },
              })

              this.results.photos += (response.data.results && response.data.results.photos) || 0
              this.results.photosSkipped += (response.data.results && response.data.results.photosSkipped) || 0
            }
          }
        }

        // Phase 3: Save diary entries
        if (diaryEntries.length > 0 && !this.cancelled) {
          this.importPhase = 'Saving diary entries...'
          this.importDetail = `${diaryEntries.length} entries`
          this.importCurrent = this.importTotal - 1

          const batchSize = 100
          for (let i = 0; i < diaryEntries.length; i += batchSize) {
            if (this.cancelled) return
            const batch = diaryEntries.slice(i, i + batchSize)

            const diaryPayload = batch.map(e => ({
              title: e.title,
              description: e.description,
              date: e.date,
              externalId: e.__externalId || null,
              sourceUrl: e.__sourceUrl || null,
            }))

            const response = await axios.post(`${this.endpoint}/importFacebookData`, {
              externalSource: 'facebook',
              photos: [],
              diary: diaryPayload,
            }, {
              headers: { Authorization: this.$store.state.jwtToken },
            })

            this.results.diary += (response.data.results && response.data.results.diary) || 0
            this.results.diarySkipped += (response.data.results && response.data.results.diarySkipped) || 0
          }

          this.importCurrent = this.importTotal
        }

        this.state = 'complete'
      } catch (err) {
        console.error('Import error:', err)
        this.error = `Import failed: ${(err.response && err.response.data && err.response.data.message) || err.message}. ${this.results.photos} photos and ${this.results.diary} diary entries were imported before the error.`
        this.state = 'preview'
      }
    },

    async uploadPhotos(photosToImport) {
      const uploaded = []
      const concurrency = 3
      const queue = [...photosToImport]

      const worker = async () => {
        while (queue.length > 0 && !this.cancelled) {
          const photo = queue.shift()
          if (!photo) break

          try {
            const entry = this.findFileInZip(photo.uri)
            if (!entry) {
              this.importCurrent++
              continue
            }

            const uint8 = await entry.async('uint8array')
            const base64 = this.uint8ToBase64(uint8)
            const mimeType = this.getMimeType(photo.uri)
            const originalName = `fb-import-${photo.uri.split('/').pop()}`

            this.importDetail = `Uploading: ${photo.uri.split('/').pop()}`

            const response = await axios.post('https://content.selfbiography.com/api/upload', {
              buffer: base64,
              originalname: originalName,
              mime: mimeType,
            }, {
              headers: { Authorization: this.$store.state.jwtToken },
            })

            if (response.data && response.data.Data && response.data.Data.Key) {
              uploaded.push({
                s3Key: response.data.Data.Key,
                title: photo.title,
                description: photo.description,
                date: photo.date,
                location: photo.location,
                externalId: photo.__externalId || null,
                sourceUrl: photo.__sourceUrl || null,
              })
            }
          } catch (err) {
            console.warn('Failed to upload photo:', photo.uri, err.message)
          }

          this.importCurrent++
        }
      }

      const workers = Array(concurrency).fill(null).map(() => worker())
      await Promise.all(workers)

      return uploaded
    },

    cancelImport() {
      this.cancelled = true
      this.state = 'preview'
    },

    // Utilities
    decodeFBText(str) {
      if (!str) return ''
      try {
        const bytes = new Uint8Array([...str].map(c => c.charCodeAt(0)))
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
      } catch (e) {
        return str
      }
    },

    formatTimestamp(ts) {
      try {
        const d = new Date(ts * 1000)
        return d.toISOString().split('T')[0]
      } catch (e) {
        return null
      }
    },

    uint8ToBase64(uint8) {
      let binary = ''
      const chunkSize = 8192
      for (let i = 0; i < uint8.length; i += chunkSize) {
        const chunk = uint8.subarray(i, i + chunkSize)
        binary += String.fromCharCode.apply(null, chunk)
      }
      return btoa(binary)
    },

    getMimeType(filename) {
      const ext = (filename || '').split('.').pop().toLowerCase()
      const mimeTypes = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        bmp: 'image/bmp',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        webm: 'video/webm',
        mkv: 'video/x-matroska',
      }
      return mimeTypes[ext] || 'image/jpeg'
    },
  },
}
</script>

<style scoped>
.border-primary {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

.rotating-icon {
  animation: pulse 1.5s ease-in-out infinite;
}

.approval-description {
  max-width: 420px;
}

.approval-type-hover {
  cursor: pointer;
}

.import-hover-preview-card {
  width: min(640px, calc(100vw - 32px));
  max-height: 70vh;
  overflow: auto;
}

.import-hover-preview-description {
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 8px;
  max-height: 220px;
  overflow: auto;
  background: #fff;
}

.import-hover-preview-json {
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 8px;
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  max-height: 220px;
  overflow: auto;
  background: #fafafa;
}

.hover-preview-video {
  width: 100%;
  max-width: 560px;
  max-height: 320px;
  background: #000;
}

.media-preview-grid {
  max-height: 360px;
  overflow-y: auto;
}

.media-thumb {
  position: relative;
  width: 96px;
  height: 96px;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
}

.media-thumb--selected {
  border-color: var(--v-primary-base, #1976d2);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}

.media-thumb-media {
  width: 96px;
  height: 96px;
  object-fit: cover;
  display: block;
}

.media-thumb-video-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-shadow: 0 0 6px rgba(0, 0, 0, 0.75);
  pointer-events: none;
}

.media-thumb-check {
  position: absolute;
  top: 2px;
  left: 2px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 3px;
  padding: 1px;
  pointer-events: none;
}

.media-thumb-size {
  position: absolute;
  bottom: 2px;
  right: 4px;
  font-size: 10px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  padding: 1px 4px;
  border-radius: 3px;
  pointer-events: none;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
